import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { SproutCardLink } from "../../../../modules/content/components/SproutCardLink/SproutCardLink";
import { SproutCommonContent } from "../../../../modules/content/components/SproutCommonContent/SproutCommonContent";
import { SproutRichText } from "../../../../modules/content/components/SproutRichText/SproutRichText";
import { SproutSection } from "../../../../modules/content/components/SproutSection/SproutSection";
import type { IIconContentGridBlockProps } from "./IconContentGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;
const spans = {
  1: "col-span-1",
  2: "sm:col-span-2",
  3: "sm:col-span-2 md:col-span-3",
  4: "sm:col-span-2 md:col-span-3 lg:col-span-4",
} as const;

export const IconContentGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: IIconContentGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <SproutSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <SproutCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-5", columns[columnCount || 3])}>
          {items.map((item, index) => (
            <SproutCardLink
              className={cn(
                "sprout-paper-lift min-h-72 p-7 md:p-8",
                index % 3 === 0 && "bg-cloud",
                index % 3 === 1 && "bg-whiteboard",
                index % 3 === 2 && "bg-sunflower/25",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <span
                className={cn(
                  "inline-flex size-14 -rotate-3 items-center justify-center rounded-[1rem_1rem_1rem_0.25rem] border-2 border-navy-ink",
                  index % 2 === 0 ? "bg-sunflower" : "bg-sky",
                )}
              >
                <Icon className="size-7 text-navy-ink" name={item.icon} />
              </span>
              <h3 className="mt-12 max-w-[18ch] font-sprout-display text-3xl font-bold leading-tight text-foreground">
                {item.heading}
              </h3>
              {item.description ? (
                <SproutRichText
                  className="mt-4 text-base text-basalt md:text-lg"
                  content={item.description}
                />
              ) : null}
            </SproutCardLink>
          ))}
        </div>
      </div>
    </SproutSection>
  );
};

IconContentGridBlock.displayName = "Block::IconContentGridBlock";
